import { type } from 'arktype'

export * from './nif.ts'

import type { OperatingZone } from '@orus.eu/operating-zone'
import type { Siret } from '@orus.eu/siret'
import { type Language, translate } from '@orus.eu/translations'

import { cifValueType, parseCifValue } from './cif.ts'
import { kvkValueType, parseKvkValue } from './kvk.ts'
import { nifValueType, parseNifValue } from './nif.ts'
import { formatSirenValue, parseSirenValue, sirenValueType } from './siren.ts'

export const companyIdNumberType = type({
  /**
   * @deprecated Use "siren" instead
   */
  type: "'provided'",
  siren: sirenValueType,
})
  .or({
    /**
     * @deprecated Use "siren" instead
     */
    type: "'inferred'",
    siren: sirenValueType,
  })
  .or({
    type: "'siren'",
    siren: sirenValueType,
  })
  .or({
    type: "'nif'",
    nif: nifValueType,
  })
  .or({
    type: "'cif'",
    cif: cifValueType,
  })
  .or({
    type: "'kvk'",
    kvk: kvkValueType,
  })
  .or({
    type: "'later'",
  })

export type CompanyIdNumber = typeof companyIdNumberType.infer

/**
 * Extract a string that is guaranteed to be a SIREN from a CompanyIdNumber.
 * Returns undefined if the CompanyIdNumber is not a siren, or if the wrapped value is invalid.
 */
export function getCompanyIdNumberSirenValue(data?: CompanyIdNumber | null | undefined): string | undefined {
  if (!data) return undefined
  switch (data.type) {
    case 'provided':
    case 'inferred':
    case 'siren':
      return parseSirenValue(data.siren) ?? undefined
    case 'kvk':
      return parseKvkValue(data.kvk) ?? undefined
    case 'nif':
    case 'cif':
    case 'later':
      return undefined
  }
}

/**
 * Extract the string value from a company id number, normalized with the parser of its type
 * (separators stripped, uppercased). Use this when comparing company id numbers: the stored value
 * may retain the user's formatting (e.g. "123 456 789"), since the validators strip before testing.
 * Returns undefined when the stored value no longer parses.
 */
export function getNormalizedCompanyIdNumberValue(data?: CompanyIdNumber | null | undefined): string | undefined {
  if (!data) return undefined
  switch (data.type) {
    case 'provided':
    case 'inferred':
    case 'siren':
      return parseSirenValue(data.siren) ?? undefined
    case 'nif':
      return parseNifValue(data.nif) ?? undefined
    case 'cif':
      return parseCifValue(data.cif) ?? undefined
    case 'kvk':
      return parseKvkValue(data.kvk) ?? undefined
    case 'later':
      return undefined
  }
}

/**
 * Extract a the string value from a company id number
 */
export function getCompanyIdNumberValue(data?: CompanyIdNumber | null | undefined): string | undefined {
  if (!data) return undefined
  switch (data.type) {
    case 'provided':
    case 'inferred':
    case 'siren':
      return data.siren
    case 'nif':
      return data.nif
    case 'cif':
      return data.cif
    case 'kvk':
      return data.kvk
    case 'later':
      return undefined
  }
}

export type CompanyIdNumberType = CompanyIdNumber['type']

/**
 * Non-deprecated CompanyIdNumberType
 */
export type CanonicalCompanyIdNumberType = Exclude<CompanyIdNumberType, 'inferred' | 'provided'>

/**
 * Extract a the string value from a company id number
 */
export function getCompanyIdNumberCanonicalType(
  data?: CompanyIdNumber | null,
): CanonicalCompanyIdNumberType | undefined {
  if (!data) return undefined
  switch (data.type) {
    case 'provided':
    case 'inferred':
    case 'siren':
      return 'siren'
    case 'nif':
    case 'cif':
    case 'kvk':
    case 'later':
      return data.type
  }
}

export const laterCompanyIdNumber = { type: 'later' } as const

export function inferCompanyIdNumberFromSiret(siret: Siret): CompanyIdNumber {
  const siren = siret.substring(0, 9)
  return { type: 'siren', siren }
}

export function formatCompanyIdNumber(
  companyIdNumber: CompanyIdNumber,
  language: Language,
  operatingZone?: OperatingZone,
): string {
  switch (companyIdNumber.type) {
    case 'provided':
    case 'inferred':
    case 'siren':
      return formatSirenValue(companyIdNumber.siren)
    case 'nif':
      return companyIdNumber.nif
    case 'cif':
      return companyIdNumber.cif
    case 'kvk':
      return companyIdNumber.kvk
    case 'later': {
      const isNlOperatingZone = operatingZone === 'nl'
      if (isNlOperatingZone) {
        return translate('subscription_funnel_kvk_no_kvk_yet', language)
      }
      return translate('waiting_for_pending_registration_process', language)
    }
  }
}
